import { CURRENCY_SYMBOL_MAP } from 'spa/shared/utils'
import {
  defineMessages,
  FormattedHTMLMessage,
  FormattedMessage,
  injectIntl,
} from 'react-intl'
import { fetchSunset, updateSunset } from '../../reducers/sunset'
import { Loading } from 'spa/components/loading'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import ActionConfirm from '../../clear_confirm/clear_confirm'
import BeatportLoginButton from './beatport_login_button'
import Modal from 'react-modal'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import styles from './styles'

const localizedMessages = defineMessages({
  beatportRefundedLink: {
    id: 'djcity.common.sunset.refunded_screen.button.link',
    defaultMessage: 'https://www.beatport.com/',
  },
  beatportCreditedLink: {
    id: 'djcity.common.sunset.credited_screen.button.link',
    defaultMessage: 'https://www.beatport.com/',
  },
  contactUsLink: {
    id: 'djcity.common.sunset.contactUsLink',
    defaultMessage: 'https://support.djcity.com/hc/en-us/requests/new',
  },
})

function SunsetMain({ intl }) {
  const dispatch = useDispatch()
  const location = useLocation()
  const [showRefundModal, setShowRefundModal] = useState(false)
  const sunsetState = useSelector(state => state.sunset)
  const [loadingDelay, setLoadingDelay] = useState(false)

  const {
    isFetching,
    isUpdating,
    error,
    sunset: {
      gotSunsetRefund,
      gotBeatportCredit,
      gotBeatportMigrated,
      sunsetRefundAmount,
      sunsetRefundCurrency,
      beatportCreditAmountInUSD,
      beatportCouponCode,
    },
  } = sunsetState

  useEffect(() => {
    dispatch(fetchSunset())
  }, [])

  useEffect(() => {
    if (loadingDelay) {
      const timer = setTimeout(() => {
        setLoadingDelay(false)
      }, 7000) // Show loading for at least 7 seconds just for effect
      return () => clearTimeout(timer)
    }
  }, [loadingDelay])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const urlAccessToken = urlParams.get('beatport_access_token')
    const urlRefreshToken = urlParams.get('beatport_refresh_token')
    const urlExpiresAt = urlParams.get('beatport_expires_at')

    if (urlAccessToken && urlRefreshToken && urlExpiresAt) {
      dispatch(
        updateSunset({
          applyBeatportCredit: true,
          beatport_access_token: urlAccessToken,
        })
      )
      window.history.replaceState({}, document.title, window.location.pathname)
      setLoadingDelay(true)
      return
    }
  }, [location.search])

  const getRefund = () => {
    dispatch(updateSunset({ applySunsetRefund: true }))
    setShowRefundModal(false)
  }

  if (error) {
    return (
      <div className={styles.sectionsContainer}>
        <div className={styles.section1}>
          <FormattedHTMLMessage
            defaultMessage="Please try again later, if the problem persists, <a href='{contactUsLink}' target='_blank' rel='noopener noreferrer'>Contact support</a>"
            id="djcity.common.sunset.error"
            values={{
              contactUsLink: intl.formatMessage(
                localizedMessages.contactUsLink
              ),
            }}
          />
        </div>
        <button
          className={styles.section3}
          onClick={() => window.location.reload()}
        >
          <FormattedMessage
            defaultMessage="TRY AGAIN"
            id="djcity.common.sunset.error.button"
          />
        </button>
      </div>
    )
  }

  if (isFetching || isUpdating || loadingDelay)
    return (
      <Loading
        className={styles.loading}
        customText={
          loadingDelay ? (
            <FormattedMessage
              defaultMessage="Migrating to Beatport in progress..."
              id="djcity.common.sunset.loading"
            />
          ) : (
            undefined
          )
        }
        intl={intl}
      />
    )

  // Main screen - no refund or credit yet
  if (
    !gotSunsetRefund &&
    !gotBeatportCredit &&
    !gotBeatportMigrated &&
    beatportCreditAmountInUSD > 0
  ) {
    return (
      <div className={styles.sectionsContainer}>
        <div className={styles.section1}>
          <FormattedMessage
            defaultMessage="All your DJcity downloads. Plus MILLIONS more tracks to discover."
            id="djcity.common.sunset.main_screen.title"
          />
        </div>
        <div className={styles.section2}>
          <FormattedHTMLMessage
            defaultMessage="Your remaining DJcity subscription gets you <span>{beatportCreditCurrency}{beatportCreditAmountInUSD}</span> BONUS credit towards Beatport streaming"
            id="djcity.common.sunset.main_screen.body"
            values={{
              beatportCreditCurrency: CURRENCY_SYMBOL_MAP.USD,
              beatportCreditAmountInUSD,
            }}
          />
        </div>
        <BeatportLoginButton
          className={styles.section3}
          text={
            <FormattedMessage
              defaultMessage="GET {beatportCreditCurrency}{beatportCreditAmountInUSD} BEATPORT CREDIT"
              id="djcity.common.sunset.main_screen.credit_button"
              values={{
                beatportCreditCurrency: CURRENCY_SYMBOL_MAP.USD,
                beatportCreditAmountInUSD,
              }}
            />
          }
        />
        {sunsetRefundAmount !== 0 ? (
          <>
            <a
              className={styles.section4}
              onClick={() => setShowRefundModal(true)}
            >
              <FormattedMessage
                defaultMessage="Request refund instead"
                id="djcity.common.sunset.main_screen.refund_button"
              />
            </a>
            <Modal className={styles.refundModal} isOpen={showRefundModal}>
              <ActionConfirm
                onClose={() => setShowRefundModal(false)}
                primaryAction={getRefund}
                primaryActionText={
                  <FormattedMessage
                    defaultMessage="CONFIRM REFUND"
                    id="djcity.common.sunset.refund_modal.button"
                  />
                }
                subtitle={
                  <FormattedHTMLMessage
                    defaultMessage="Your DJcity refund amount is <span>{sunsetRefundCurrency}{sunsetRefundAmount}</span>."
                    id="djcity.common.sunset.refund_modal.body"
                    values={{
                      sunsetRefundCurrency:
                        CURRENCY_SYMBOL_MAP[sunsetRefundCurrency],
                      sunsetRefundAmount:
                        sunsetRefundCurrency === 'JPY' ||
                        Number.isInteger(sunsetRefundAmount)
                          ? sunsetRefundAmount.toFixed(0)
                          : sunsetRefundAmount.toFixed(2),
                    }}
                  />
                }
                title={
                  <FormattedHTMLMessage
                    defaultMessage={`YOU'RE ABOUT TO LOSE ACCESS TO <span>{beatportCreditCurrency}{beatportCreditAmountInUSD}</span> IN BEATPORT BONUS CREDITS`}
                    id="djcity.common.sunset.refund_modal.title"
                    values={{
                      beatportCreditCurrency: CURRENCY_SYMBOL_MAP.USD,
                      beatportCreditAmountInUSD,
                    }}
                  />
                }
              />
            </Modal>
          </>
        ) : null}
      </div>
    )
  }

  // Refunded but not migrated yet
  if (gotSunsetRefund) {
    return (
      <div className={styles.sectionsContainer}>
        <div className={styles.section1}>
          <FormattedHTMLMessage
            defaultMessage="Your account has already been refunded <span>{sunsetRefundCurrency}{sunsetRefundAmount}</span>."
            id="djcity.common.sunset.refunded_screen.title"
            values={{
              sunsetRefundCurrency: CURRENCY_SYMBOL_MAP[sunsetRefundCurrency],
              sunsetRefundAmount:
                sunsetRefundCurrency === 'JPY' ||
                Number.isInteger(sunsetRefundAmount)
                  ? sunsetRefundAmount.toFixed(0)
                  : sunsetRefundAmount.toFixed(2),
            }}
          />
        </div>
        <button
          className={styles.section3}
          onClick={() =>
            window.open(
              intl.formatMessage(localizedMessages.beatportRefundedLink),
              '_blank'
            )
          }
        >
          <FormattedMessage
            defaultMessage="GO TO BEATPORT"
            id="djcity.common.sunset.refunded_screen.button"
          />
        </button>
      </div>
    )
  }

  if (
    !gotSunsetRefund &&
    !gotBeatportCredit &&
    beatportCreditAmountInUSD === 0
  ) {
    return (
      <div className={styles.sectionsContainer}>
        <button
          className={styles.section3}
          onClick={() =>
            window.open(
              intl.formatMessage(localizedMessages.beatportCreditedLink),
              '_blank'
            )
          }
        >
          <FormattedMessage
            defaultMessage="GO TO BEATPORT"
            id="djcity.common.sunset.credited_screen.button"
          />
        </button>
      </div>
    )
  }

  return (
    <div className={styles.sectionsContainer}>
      <div className={styles.section1}>
        <FormattedHTMLMessage
          defaultMessage="Your account has already been credited <span>{beatportCreditCurrency}{beatportCreditAmountInUSD}</span>."
          id="djcity.common.sunset.credited_screen.title"
          values={{
            beatportCreditCurrency: CURRENCY_SYMBOL_MAP.USD,
            beatportCreditAmountInUSD,
          }}
        />
      </div>
      <div className={styles.section2_alt}>
        <FormattedHTMLMessage
          defaultMessage="{beatportCouponCode}"
          id="djcity.common.sunset.credited_screen.body"
          values={{
            beatportCouponCode,
          }}
        />
      </div>
      <button
        className={styles.section3}
        onClick={() =>
          window.open(
            intl.formatMessage(localizedMessages.beatportCreditedLink),
            '_blank'
          )
        }
      >
        <FormattedMessage
          defaultMessage="GO TO BEATPORT"
          id="djcity.common.sunset.credited_screen.button"
        />
      </button>
    </div>
  )
}

SunsetMain.propTypes = {
  intl: PropTypes.object,
}

export default injectIntl(SunsetMain)
